import React, { useEffect, useState } from "react";
import "./privacy.css";
import usePageMeta from "../../../hooks/usePageMeta";

export default function PrivacyPolicy() {
  usePageMeta(
    "Privacy Policy | MakeBeeTrip",
    "Read the MakeBeeTrip Privacy Policy explaining how we collect, use, store and protect personal information when providing travel services.",
    "makebeetrip privacy policy, personal information, travel privacy, data protection, booking privacy"
  );

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const privacySections = [
    {
      title: "About MakeBeeTrip",
      content: (
        <>
          <p>
            MakeBeeTrip provides travel-related services including:
          </p>

          <ul>
            <li>Flight bookings</li>
            <li>Hotel bookings</li>
            <li>Holiday packages</li>
            <li>Cab and transfer services</li>
            <li>Bus bookings</li>
            <li>Visa assistance and services</li>
            <li>Customized travel arrangements</li>
            <li>Travel-related customer support</li>
          </ul>

          <p>
            For any privacy-related questions, you can contact us using the
            details provided below.
          </p>

          <div className="privacy-contact-card">
            <p>
              <strong>Website:</strong>{" "}
              <a
                href="https://makebeetrip.com/"
                target="_blank"
                rel="noreferrer"
              >
                https://makebeetrip.com/
              </a>
            </p>

            <p>
              <strong>Phone:</strong>{" "}
              <a href="tel:+919355471999">+91 93554 71999</a>
            </p>

            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:Holidays@makebeetrip.com">
                Holidays@makebeetrip.com
              </a>
            </p>
          </div>
        </>
      ),
    },

    {
      title: "Information We Collect",
      content: (
        <>
          <p>
            Depending on the service you request, we may collect the following
            information.
          </p>

          <h3>Personal Information</h3>

          <ul>
            <li>Full name</li>
            <li>Date of birth</li>
            <li>Gender</li>
            <li>Mobile number</li>
            <li>Email address</li>
            <li>Residential or correspondence address</li>
            <li>Nationality</li>
          </ul>

          <h3>Travel &amp; Booking Information</h3>

          <ul>
            <li>Travel dates</li>
            <li>Destination and itinerary</li>
            <li>Flight details</li>
            <li>Hotel preferences</li>
            <li>Passenger details</li>
            <li>Traveller information</li>
            <li>Visa-related information</li>
            <li>
              Passport details and documents where required for international
              travel or visa processing
            </li>
          </ul>

          <h3>Payment Information</h3>

          <p>
            When you make a payment, certain payment-related information may
            be processed through our payment gateway, banking partner or other
            authorized payment service provider.
          </p>

          <p>
            We generally do not store complete debit/credit card, CVV or
            banking credentials on our own servers unless specifically
            required and permitted by applicable law.
          </p>

          <h3>Technical Information</h3>

          <p>
            When you visit our website, certain technical information may
            automatically be collected, such as:
          </p>

          <ul>
            <li>IP address</li>
            <li>Browser type</li>
            <li>Device information</li>
            <li>Operating system</li>
            <li>Website usage information</li>
            <li>Pages visited</li>
            <li>Date and time of access</li>
            <li>Referring website</li>
          </ul>
        </>
      ),
    },

    {
      title: "How We Use Your Information",
      content: (
        <>
          <p>We may use your information to:</p>

          <ul>
            <li>
              Process flight, hotel, holiday, cab, bus and visa requests
            </li>
            <li>Prepare quotations and itineraries</li>
            <li>Complete travel bookings</li>
            <li>Process visa applications and related documentation</li>
            <li>Communicate booking confirmations and updates</li>
            <li>Contact you regarding your enquiry or booking</li>
            <li>Process payments and refunds</li>
            <li>Provide customer support</li>
            <li>Improve our website and services</li>
            <li>Prevent fraud and unauthorized transactions</li>
            <li>
              Comply with applicable legal and regulatory requirements
            </li>
            <li>Send service-related communications</li>
            <li>
              Send promotional offers, travel deals and marketing
              communications where permitted by law or where you have provided
              consent
            </li>
          </ul>
        </>
      ),
    },

    {
      title: "Sharing of Information",
      content: (
        <>
          <p>
            We may share relevant information with trusted third parties when
            necessary to provide the requested travel service.
          </p>

          <p>These may include:</p>

          <ul>
            <li>Airlines</li>
            <li>Hotels and accommodation providers</li>
            <li>Bus operators</li>
            <li>Cab and transfer providers</li>
            <li>Visa processing agencies</li>
            <li>Destination management companies</li>
            <li>Tour operators</li>
            <li>Payment gateways</li>
            <li>Technology and service providers</li>
            <li>
              Government authorities, embassies or consulates where required
              for visa or travel processing
            </li>
            <li>
              Legal, regulatory or law-enforcement authorities where required
              by law
            </li>
          </ul>

          <p>
            We only share information that is reasonably necessary for the
            relevant service, transaction or legal requirement.
          </p>
        </>
      ),
    },

    {
      title: "Visa and Passport Information",
      content: (
        <>
          <p>
            For visa-related services, you may be required to provide sensitive
            travel documents, including passport copies, photographs, financial
            documents, employment information, travel history or other
            supporting documents.
          </p>

          <p>
            Such information may be shared with the relevant embassy,
            consulate, visa application centre, authorized visa partner or
            service provider for processing your application.
          </p>

          <p>
            MakeBeeTrip does not guarantee the approval, processing time or
            decision of any visa application, as such decisions are made by
            the relevant government authority or visa-issuing authority.
          </p>
        </>
      ),
    },

    {
      title: "Cookies",
      content: (
        <>
          <p>
            Our website may use cookies and similar technologies to improve
            user experience, understand website traffic and provide relevant
            functionality.
          </p>

          <p>Cookies may help us:</p>

          <ul>
            <li>Remember user preferences</li>
            <li>Improve website performance</li>
            <li>Understand how visitors use our website</li>
            <li>Measure marketing campaigns</li>
            <li>Improve our services and content</li>
          </ul>

          <p>
            You can manage or disable cookies through your browser settings.
            Disabling certain cookies may affect some website functionality.
          </p>
        </>
      ),
    },

    {
      title: "Third-Party Services and Links",
      content: (
        <>
          <p>
            Our website may contain links, integrations or services provided
            by third parties, including airlines, hotels, payment providers,
            social media platforms, travel partners or other service
            providers.
          </p>

          <p>
            MakeBeeTrip is not responsible for the privacy practices, policies
            or content of third-party websites.
          </p>

          <p>
            We recommend reviewing the privacy policy of any third-party
            website before providing your personal information.
          </p>
        </>
      ),
    },

    {
      title: "Data Security",
      content: (
        <>
          <p>
            We take reasonable technical and organizational measures to
            protect your personal information against unauthorized access,
            misuse, alteration, disclosure or destruction.
          </p>

          <p>
            However, no method of transmission over the internet or electronic
            storage system can be guaranteed to be completely secure.
          </p>

          <p>
            Therefore, while we take reasonable steps to protect your
            information, we cannot guarantee absolute security.
          </p>
        </>
      ),
    },

    {
      title: "Data Retention",
      content: (
        <>
          <p>
            We retain personal information only for as long as reasonably
            necessary to:
          </p>

          <ul>
            <li>Provide requested services</li>
            <li>Maintain booking and transaction records</li>
            <li>Fulfil legal and regulatory obligations</li>
            <li>Resolve disputes</li>
            <li>Prevent fraud</li>
            <li>Enforce agreements</li>
            <li>Maintain legitimate business records</li>
          </ul>

          <p>
            Once information is no longer required, it may be deleted,
            anonymized or securely disposed of, subject to applicable legal
            requirements.
          </p>
        </>
      ),
    },

    {
      title: "Marketing Communications",
      content: (
        <>
          <p>
            With your consent or where otherwise permitted by applicable law,
            we may contact you through:
          </p>

          <ul>
            <li>Phone calls</li>
            <li>SMS</li>
            <li>WhatsApp</li>
            <li>Email</li>
            <li>Other digital communication channels</li>
          </ul>

          <p>
            You may request to stop receiving promotional communications at
            any time by contacting us.
          </p>

          <p>
            Please note that you may still receive essential service-related
            communications regarding bookings, payments, cancellations, visa
            processing or other transactions.
          </p>
        </>
      ),
    },

    {
      title: "Your Rights",
      content: (
        <>
          <p>
            Depending on applicable law, you may have rights regarding your
            personal information, including the right to:
          </p>

          <ul>
            <li>Request access to information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>
              Request deletion of information where legally applicable
            </li>
            <li>
              Withdraw consent where processing is based on consent
            </li>
            <li>Opt out of promotional communications</li>
            <li>Raise a privacy-related complaint or concern</li>
          </ul>

          <p>
            To exercise these rights, please contact us using the details
            provided below.
          </p>
        </>
      ),
    },

    {
      title: "Children's Privacy",
      content: (
        <>
          <p>
            Our services are intended to be used by adults and travellers
            acting with appropriate authorization.
          </p>

          <p>
            We do not knowingly collect personal information from children
            without appropriate consent or involvement of a parent or legal
            guardian where required.
          </p>
        </>
      ),
    },

    {
      title: "International Data Processing",
      content: (
        <>
          <p>
            Some travel services, including international flights, hotels and
            visa processing, may require your information to be shared with
            service providers or authorities located outside India.
          </p>

          <p>
            By using our services, you understand that such transfers may be
            necessary to complete your requested travel or visa service and
            may be subject to the laws and privacy practices applicable in
            those jurisdictions.
          </p>
        </>
      ),
    },

    {
      title: "Fraud Prevention",
      content: (
        <>
          <p>
            We may use personal and transaction information to detect,
            investigate and prevent:
          </p>

          <ul>
            <li>Fraudulent bookings</li>
            <li>Payment fraud</li>
            <li>Identity misuse</li>
            <li>Unauthorized transactions</li>
            <li>Abuse of our website or services</li>
          </ul>

          <p>
            Information may be shared with payment providers, service partners
            or competent authorities where reasonably necessary.
          </p>
        </>
      ),
    },

    {
      title: "Changes to This Privacy Policy",
      content: (
        <>
          <p>
            We may update this Privacy Policy from time to time to reflect
            changes in our services, technology, legal requirements or
            business practices.
          </p>

          <p>
            Any updated version will be published on this page with a revised{" "}
            <strong>Effective Date</strong>.
          </p>

          <p>
            We encourage you to review this Privacy Policy periodically.
          </p>
        </>
      ),
    },

    {
      title: "Contact Us",
      contact: true,
      content: (
        <div className="privacy-contact">
          <p>
            If you have any questions, concerns, requests or complaints
            regarding this Privacy Policy or the handling of your personal
            information, please contact us:
          </p>

          <div className="privacy-contact-card">
            <p>
              <strong>MakeBeeTrip Private Limited</strong>
            </p>

            <p>
              <strong>Phone:</strong>{" "}
              <a href="tel:+919355471999">+91 93554 71999</a>
            </p>

            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:Holidays@makebeetrip.com">
                Holidays@makebeetrip.com
              </a>
            </p>

            <p>
              <strong>Website:</strong>{" "}
              <a
                href="https://makebeetrip.com/"
                target="_blank"
                rel="noreferrer"
              >
                https://makebeetrip.com/
              </a>
            </p>

            <div className="privacy-address">
              <strong>Registered/Mailing Address:</strong>

              <span>
                WZ A-1/1, G/F R/P, Vikas Puri, New Delhi, West Delhi – 110018,
                Delhi, India
              </span>
            </div>
          </div>

          <p className="privacy-contact-response">
            We will make reasonable efforts to respond to privacy-related
            requests within a reasonable period.
          </p>

          <div className="privacy-tagline">
            <strong>MakeBeeTrip</strong>
            <span>Your Journey, Our Commitment.</span>
          </div>
        </div>
      ),
    },

    {
      title: "Consent",
      content: (
        <>
          <p>
            By using our website or submitting your information to MakeBeeTrip,
            you acknowledge that you have read and understood this Privacy
            Policy and agree to the collection and use of your information as
            described herein, subject to applicable law.
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="privacy-page">
      {/* Sticky Header */}
      <header className="privacy-header">
        <div className="privacy-header-inner">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="privacy-back-button"
            aria-label="Go back"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>

          <div className="privacy-brand-pill">
            <span>MakeBeeTrip</span>

            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="privacy-hero">
        <div className="privacy-hero-glow" />

        <div className="privacy-hero-content">
          <span className="privacy-hero-label">
            MakeBeeTrip Private Limited
          </span>

          <h1>Privacy Policy</h1>

          <p className="privacy-hero-subtitle">
            Your Privacy Matters To Us
          </p>

          <p className="privacy-hero-description">
            This Privacy Policy explains how MakeBeeTrip collects, uses,
            stores and protects personal information when you use our website,
            services and travel products.
          </p>

          <div className="privacy-effective-date">
            Effective Date: 29 September 2026
          </div>
        </div>
      </section>

      {/* Main */}
      <main className="privacy-main">
        {/* Introduction */}
        <section className="privacy-intro">
          <p>
            Welcome to <strong>MakeBeeTrip</strong>. We respect your privacy
            and are committed to protecting the personal information you share
            with us while using our website, services and travel products.
          </p>

          <p>
            This Privacy Policy explains how we collect, use, store and
            protect your information when you visit or use{" "}
            <a
              href="https://makebeetrip.com/"
              target="_blank"
              rel="noreferrer"
            >
              https://makebeetrip.com/
            </a>{" "}
            or contact us for our travel services.
          </p>
        </section>

        {/* Desktop */}
        <div className="privacy-desktop-sections">
          {privacySections.map((section, index) => (
            <article
              key={index}
              className={`privacy-section-card ${
                section.contact ? "privacy-contact-section" : ""
              }`}
            >
              <div className="privacy-section-heading">
                <span className="privacy-section-number">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <h2>{section.title}</h2>
              </div>

              <div className="privacy-section-content">
                {section.content}
              </div>
            </article>
          ))}
        </div>

        {/* Mobile */}
        <div className="privacy-mobile-sections">
          {privacySections.map((section, index) => {
            const isOpen = openAccordion === index;

            return (
              <article
                key={index}
                className={`privacy-mobile-card ${
                  section.contact ? "privacy-contact-section" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleAccordion(index)}
                  className="privacy-mobile-trigger"
                  aria-expanded={isOpen}
                >
                  <span className="privacy-mobile-title">
                    <span className="privacy-section-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <span>{section.title}</span>
                  </span>

                  <svg
                    className={`privacy-chevron ${
                      isOpen ? "privacy-chevron-open" : ""
                    }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                <div
                  className={`privacy-mobile-content ${
                    isOpen ? "privacy-mobile-content-open" : ""
                  }`}
                >
                  <div>{section.content}</div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Final */}
        <div className="privacy-final-disclaimer">
          <p>
            This Privacy Policy may be updated from time to time to reflect
            changes in our services, technology, legal requirements or
            business practices. Any updated version will be published on this
            page with a revised Effective Date.
          </p>
        </div>
      </main>

      {/* Back To Top */}
      <button
        type="button"
        onClick={scrollToTop}
        className={`privacy-back-to-top ${
          showScrollTop ? "privacy-back-to-top-visible" : ""
        }`}
        aria-label="Back to Top"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          />
        </svg>
      </button>
    </div>
  );
}